import {
  CHECK_CELL_WIDTH,
  CheckboxVisibility,
  DEFAULT_CELL_STYLE_PROPS,
  IShimmeredDetailsListProps,
  SelectionMode,
  ShimmeredDetailsList,
} from "@fluentui/react";
import React, { useEffect, useRef, useState } from "react";

// Width Fluent gives a column that declares no minWidth (not exported by Fluent).
const MIN_COLUMN_WIDTH = 100;

// Fluent's justified layout caps a widened column at what still fits on the page; once the
// user's widths no longer fit, lay the table out on a wider viewport so it scrolls instead.
const ResizableDetailsList: React.FC<IShimmeredDetailsListProps> = ({
  columns,
  onColumnResize,
  ...props
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [resizedWidths, setResizedWidths] = useState<{
    [columnKey: string]: number;
  }>({});

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const measure = () => setContainerWidth(container.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const resizableColumns = columns?.map((column) => ({
    ...column,
    isResizable: column.isResizable ?? true,
  }));

  // Mirrors Fluent's own column-layout arithmetic.
  const cellStyleProps = props.cellStyleProps ?? DEFAULT_CELL_STYLE_PROPS;
  const hasRowCheck =
    props.selectionMode !== SelectionMode.none &&
    props.checkboxVisibility !== CheckboxVisibility.hidden;
  const requiredWidth = (resizableColumns ?? []).reduce(
    (width, column) =>
      width +
      (resizedWidths[column.key] ?? (column.minWidth || MIN_COLUMN_WIDTH)) +
      cellStyleProps.cellLeftPadding +
      cellStyleProps.cellRightPadding +
      (column.isPadded ? cellStyleProps.cellExtraRightPadding : 0),
    hasRowCheck ? CHECK_CELL_WIDTH : 0,
  );
  const overflows =
    Object.keys(resizedWidths).length > 0 &&
    containerWidth > 0 &&
    requiredWidth > containerWidth;

  return (
    <div ref={containerRef}>
      <ShimmeredDetailsList
        {...props}
        // Pass a viewport only when overriding it: even an undefined one would replace the
        // width Fluent measures on its own.
        {...(overflows
          ? { viewport: { width: requiredWidth, height: window.innerHeight } }
          : {})}
        columns={resizableColumns}
        onColumnResize={(column, newWidth, columnIndex) => {
          if (column && newWidth !== undefined) {
            setResizedWidths((widths) => ({
              ...widths,
              [column.key]: newWidth,
            }));
          }
          onColumnResize?.(column, newWidth, columnIndex);
        }}
      />
    </div>
  );
};

export default ResizableDetailsList;
